/**
 * The model catalogue — the sport × mode grid.
 *
 * This file is the single source of truth for what exists. Every count, list
 * and badge on the marketing and pricing pages derives from it, so the copy
 * cannot claim a model that isn't built.
 *
 * TWO SEPARATE AXES, and conflating them is the bug this design prevents:
 *   build       — reality.  Does the model exist and answer questions?
 *   purchasable — policy.   Are we willing to sell it?
 *
 * The invariant (enforced in catalogue.test.ts): a model that isn't built can
 * never carry a price. Reality gates policy, never the other way round.
 *
 * Status as at 2026-07-16, from plans/00-master-schedule.md.
 */

/**
 * Types come from the wire contract, not from here.
 *
 * This file is SEED DATA for an API response — MSW serves it as GET /catalogue
 * today, a Lambda serves the same shape in pass 2. Declaring a parallel
 * `ModelEntry` here would give the same concept two definitions that drift
 * silently. The schema owns the shape; this file owns the facts.
 *
 * On dataProvider: API-Sports across all four codes — it covers association
 * football's European leagues on the same key. nflverse was evaluated for
 * NFL and rejected on the licensed-sources-only rule (2026-07-16) — do not
 * reintroduce it. plans/00-master-schedule.md §NFL is stale and still describes
 * the nflverse path; the code (nfl/ingest_nfl.py, nfl/README.md) is the authority.
 */
export type {
  BuildStatus,
  Mode,
  ModelEntry,
  ModelId,
  Sport,
} from "../services/api/schemas";

import type { Mode, ModelEntry, Sport } from "../services/api/schemas";
import { STANDARD_PER_MODEL_MONTHLY_AUD } from "./pricing";

/**
 * Seed entries omit `capabilities` — the API layer attaches them (see
 * mocks/fixtures/capabilities.ts), because prompt chips and their credit
 * estimates are a serving concern, not a fact about what exists.
 *
 * Exported because the card renders either a seed (landing page, straight from
 * config) or a fetched entry (directory, via GET /catalogue). A full ModelEntry
 * is assignable to this, so one component serves both.
 */
export type CatalogueSeed = Omit<ModelEntry, "capabilities">;

/**
 * A fixture's `competition` as the reader should see it.
 *
 * The published feed carries a SLUG — "seriea", "laliga", "ligue1", "epl" —
 * because it is a key, and the landing page was rendering it verbatim, so the
 * league column read SERIEA / LALIGA / LIGUE1 down the page. It looked fine
 * locally for the same reason three schema mismatches did: the mock said
 * "Premier League" where production says "epl". The mock was tidier than
 * production in a cosmetic key rather than a structural one.
 *
 * A DISPLAY map rather than a change to the exporter, deliberately. The slug is
 * the identity three sports' exporters and the fixture-page routes all key off;
 * renaming it in the feed to make a column read nicely would be changing a key
 * to fix a label.
 *
 * An unknown slug falls back to the slug itself, upper-cased by the existing
 * column styling — wrong-looking, which is the point: a new competition should
 * be visibly unlabelled rather than silently pretty.
 */
export const COMPETITION_LABEL: Record<string, string> = {
  epl: "Premier League",
  laliga: "La Liga",
  seriea: "Serie A",
  bundesliga: "Bundesliga",
  ligue1: "Ligue 1",
  nfl: "NFL",
  nba: "NBA",
  afl: "AFL",
};

export function competitionLabel(slug: string): string {
  return COMPETITION_LABEL[slug?.toLowerCase?.() ?? ""] ?? slug;
}

export const SPORT_LABEL: Record<Sport, string> = {
  afl: "AFL",
  nba: "NBA",
  nfl: "NFL",
  /* Ned's word, and the one the audience uses. It is also the one word in this
     map that does not name a single competition: "AFL", "NBA" and "NFL" each
     ARE a league, and this is six of them. The blurbs carry the scope, which is
     why they name the countries rather than saying "Europe". */
  football: "Football",
};

export const MODE_LABEL: Record<Mode, string> = {
  sport: "Sport",
  fantasy: "Fantasy",
};

export const PERSONA_LABEL = {
  kev: "Kev",
  franco: "Franco",
} as const;

/**
 * Prices are MONTHLY, charged monthly (Ned, 2026-08-31; $4.99 since 2026-09-03): $4.99 a month, 450
 * credits, and a daily token budget worth about 3 games. "About", because since
 * 2026-08-28 the server budgets TOKENS and not fixtures, so the game count is
 * what a typical session yields rather than a number anyone is owed — see
 * plans.ts. There is no second rate: the weekly ladder this comment used to name
 * ($14.99/wk, billed $64.96) was retired with the move to monthly pricing.
 *
 * The rates live in ./pricing. Don't re-declare one here — a second copy of a
 * price is how the page and the invoice drift apart.
 *
 * The priceIds below are real Stripe **LIVE-MODE** prices. Their recurring
 * amount is the SAME figure shown on the page, because prices are quoted and
 * charged monthly since 2026-08-31; there is no longer a weekly headline and a
 * monthly charge to keep in agreement. There is no promotional price in this
 * file: every entry advertises its standing rate, and the founding discount is a
 * Stripe COUPON (30% off, capped at the first 20 redemptions) rather than a
 * second price or a second field here.
 *
 * ⚠️ These ids are LIVE, and this seed is compiled into every build including
 * dev's. That is safe only because nothing buys from this file: `useCatalogue`
 * revalidates against `GET /catalogue` on mount and the server answers with the
 * ids for ITS environment (RIPK `api/kev_prices.py` — dev overrides the live ids
 * with test-mode ones). The seed is a fast first frame, and `Pricing.tsx` will
 * not let anyone buy from it; see the note there.
 *
 * A Stripe price is immutable once used, and test and live ids never
 * interchange, so a re-price is new ids here rather than an edit.
 * `kev-docs/kev-stripe-setup.md` is the procedure.
 */
export const STANDARD_PER_MODEL_PRICE_AUD = STANDARD_PER_MODEL_MONTHLY_AUD;



export const CATALOGUE: readonly CatalogueSeed[] = [
  {
    id: "nba-fantasy",
    sport: "nba",
    mode: "fantasy",
    persona: "franco",
    name: "NBA Fantasy",
    /* Franco is fantasy and nothing else (Ned, 2026-07-31). Scoring, captaincy,
       trades, waivers. Anything about a game result or a prop belongs to Kev. */
    blurb: "Fantasy scoring, captaincy and who steps up when someone sits, straight off the game log.",
    build: "beta",
    /* OFF SALE 2026-10-06 (Ned): Premium and Pro are the only subscriptions on
       sale. `beta` keeps it reachable to both tiers; existing per-model
       subscribers keep access through their SUB#MODEL row. The priceId stays so
       re-opening sales is this one flag, in both repos (RIPK api/catalogue.json). */
    purchasable: false,
    /* REPRICED 2026-09-03 (Ned) to $4.99 per model, $9.99 Premium, $19.99 Pro, monthly.
       Six new live prices, all AUD, monthly, tax_behavior=inclusive, verified by id
       against the live account before being written here; the 2026-08-31 ids are
       archived in Stripe. */
    priceId: "price_1UBZSp2Ni8JNdVHwVfErZfnZ",
    monthlyPriceAud: STANDARD_PER_MODEL_PRICE_AUD,
    dataProvider: "api-sports",
  },
  {
    id: "nba-sport",
    sport: "nba",
    mode: "sport",
    persona: "kev",
    name: "NBA Sport",
    /* Kev covers player props as well as team totals (Ned, 2026-07-31). */
    blurb: "Player props and team totals, with the uncertainty stated up front.",
    build: "beta",
    /* OFF SALE 2026-10-06 (Ned): Premium and Pro are the only subscriptions on
       sale. `beta` keeps it reachable to both tiers; existing per-model
       subscribers keep access through their SUB#MODEL row. The priceId stays so
       re-opening sales is this one flag, in both repos (RIPK api/catalogue.json). */
    purchasable: false,
    /* Repriced to $4.99/month, 2026-09-03 (was $19.99 from 2026-08-31). Verified
       against the live account before the swap: active, AUD, monthly,
       unit_amount 499, tax_behavior inclusive, on prod_Uz92WNgTy57sV6 ("KEV NBA
       Sport").

       The id this replaced was itself a 2026-08-29 swap to fix a tax treatment,
       and the note it carried is worth keeping in spirit: this file and RIPK's
       catalogue.json must swap TOGETHER, because the browser sends the id from
       one and the server validates it against the other. A mismatch is refused
       `price_grant_mismatch` in prod, which has happened twice in this file's
       history and in the same direction both times. */
    priceId: "price_1UBZTn2Ni8JNdVHwHPhafGIw",
    monthlyPriceAud: STANDARD_PER_MODEL_PRICE_AUD,
    dataProvider: "api-sports",
  },
  {
    id: "afl-fantasy",
    sport: "afl",
    mode: "fantasy",
    persona: "franco",
    name: "AFL Fantasy",
    blurb: "Captaincy, trades and projected points for AFL Fantasy.",
    build: "soon",
    purchasable: false,
    dataProvider: "api-sports",
  },
  {
    id: "afl-sport",
    sport: "afl",
    mode: "sport",
    persona: "kev",
    name: "AFL Sport",
    /* "calibrated and scored against the record" came out 2026-08-12. It was the
       only blurb of the five claiming performance rather than describing
       function, and it claimed it for a model that isn't built — a track record
       for something with no record. Blurbs say what a model answers. */
    blurb: "Match-winner probabilities and player props.",
    build: "soon",
    purchasable: false,
    dataProvider: "api-sports",
  },
  {
    id: "nfl-fantasy",
    sport: "nfl",
    mode: "fantasy",
    persona: "franco",
    name: "NFL Fantasy",
    blurb: "Season-long and DFS: usage, matchups and waiver targets.",
    /* MOVED `soon` -> `beta` ON 2026-09-07. It is built and it answers:
       kev-nfl-chat serves Franco's NFL persona in dev with per-position
       projections, a VOR draft board, the week's slate and a one-call roster
       review. `off-roadmap` was never the claim here — nflverse is still
       rejected on the licensed-sources-only rule and this runs on API-Sports,
       the same feed the entry always named.

       `purchasable: false` STAYS, and not because anything is pending: no
       Stripe price exists for either NFL model, and creating one is a decision
       rather than a code change. `beta` means reachable to an entitled reader
       — Premium, Pro, or hand-granted in dev — not sellable. Reachable and
       sellable are different questions, the same split the football block
       below spells out.

       ⚠️ features/fantasy/rules.ts MOVES WITH THIS FLAG, NOT AFTER IT.
       `canAccess` checks `build` before anything else, so a `live` fantasy room
       over a `soon` catalogue entry shows a reader four prompts and then has the
       chat refuse every one of them. */
    build: "beta",
    purchasable: false,
    dataProvider: "api-sports",
  },
  {
    id: "nfl-sport",
    sport: "nfl",
    mode: "sport",
    persona: "kev",
    name: "NFL Sport",
    blurb: "Player props and team totals.",
    build: "beta",
    purchasable: false,
    dataProvider: "api-sports",
    /* ── NED'S CALL, 2026-09-13: "make the NFL accessible from the premium and
       pro models only". `beta` + `purchasable: false` IS that sentence in this
       file's vocabulary, and no other pair of values says it.

       `canAccess` admits pro and premium to any model that is not `soon` or
       `off-roadmap`, and `upsellsFor` offers an individual subscription only
       when `purchasable` is true. So this entry is now reachable by a tier
       holder and buyable by nobody — which is what the server has said since
       2026-09-05, and what nfl-fantasy directly above has said all along.

       ⚠️ THIS REVERSES THE NOTE THAT USED TO SIT HERE, and the reversal is a
       product decision rather than a correction of one. On 2026-09-10 this was
       flipped to `beta` to match the server, four tests refused it, and it was
       flipped back with the reasoning that being deployed is not the same as
       being offered. That reasoning was sound and is not what changed: Ned has
       now decided NFL IS offered, to tier holders. Whoever reads
       plans/00-master-schedule.md next and finds it still saying otherwise
       should update the schedule rather than this file.

       The rail rule that those tests guarded — off-roadmap codes are omitted
       rather than filed under "Coming soon" — is unchanged and still
       implemented in features/board/sports.ts. It is now covered by a fixture
       in sports.test.ts instead of by whichever real model happens to be
       off-roadmap, because nothing in the catalogue is any more. */
  },
  /* ── FOOTBALL: the top five European leagues, and the Champions League ─────
     Added 2026-08-28 (Ned: "Use the Top 5 European Leagues and UCL inference").

     BOTH MOVED `soon` -> `beta` ON 2026-08-29. They are built: RIPK `soccer/`
     carries the ingest for two providers, the normalisers, an eleven-check DQ
     gate, the scoreline and prop models, and calibration maps — all validated on
     held-out fixtures BY DATE. 176 tests. The legal review of the guardrails
     delta landed the same day.

     Still `purchasable: false`, and not because anything is pending: no Stripe
     price exists for either model, and creating one is a decision rather than a
     code change. Reachable and sellable are different questions.

     ⚠️ TWO CLAIMS WERE REMOVED FROM THE BLURBS, on our own evidence rather than
     the lawyer's. The legal review passed everything; the CALIBRATION gate did
     not (kev-football-guardrails-delta.md §5.1):

       · TOTAL GOALS and BOTH TEAMS TO SCORE. Held out, they beat the base rate
         in three of seven competitions and LOST in four, with sub-1.5% margins
         where they won. There is no edge, so the word "goals" must not appear
         in a blurb — this file is what the copy derives from, and a pricing page
         advertising a market we decline to sell is the failure mode the
         build/purchasable split exists to prevent.
       · THE CHAMPIONS LEAGUE. See below — the "UCL inference" note was right,
         and it is precisely why the naive version cannot ship.

     "UCL inference" is Ned's term, and the 2026-08-29 validation confirmed the
     reasoning exactly. Fitted as its own competition, the Champions League is
     1,810 fixtures spread over 224 teams — a median of eight matches each, so
     448 parameters from 1,810 results. Its result markets survive weakly (+1.6%)
     because most of that signal is "the good team is playing the bad team", but
     totals score -8.1%: WORSE than assuming the league average every time,
     because a total needs absolute goal rates and a fit that sparse cannot
     identify them.

     So the note was correct that ratings must come from domestic play. What does
     not yet exist is the cross-league seeding that would make it work, and until
     it does the blurb cannot promise it. The Champions League is out of the
     copy — a data problem, not a licence one. */
  {
    id: "football-sport",
    sport: "football",
    mode: "sport",
    persona: "kev",
    name: "Football Sport",
    /* "Match results" not "goals", and no Champions League — see the block
       comment above. Player props are Premier League only: the Sportmonks plan
       that supplies player detail carries the Premier League, the Championship
       and the two domestic cups, so claiming props "across the top five" would
       promise four leagues of data we do not have. */
    blurb:
      "Match-winner probabilities across the top five European leagues, " +
      "plus Premier League player props.",
    build: "beta",
    /* ON SALE since 2026-08-29, matching RIPK a4c31f2 — the runner exists, which
       is what `SellingItMeansTheRunnerCanCheckIt` refuses to let this flag get
       ahead of. GST-inclusive price, verified live before it was pasted. */
    /* OFF SALE 2026-10-06 (Ned): Premium and Pro are the only subscriptions on
       sale. `beta` keeps it reachable to both tiers; existing per-model
       subscribers keep access through their SUB#MODEL row. The priceId stays so
       re-opening sales is this one flag, in both repos (RIPK api/catalogue.json). */
    purchasable: false,
    priceId: "price_1UBZRc2Ni8JNdVHw2gtY6orB",
    monthlyPriceAud: STANDARD_PER_MODEL_PRICE_AUD,
    dataProvider: "api-sports",
  },
  {
    id: "football-fantasy",
    sport: "football",
    mode: "fantasy",
    persona: "franco",
    name: "Football Fantasy",
    /* Scoped to the Premier League on purpose. Fantasy football at any scale
       people actually play is FPL; a "top five leagues fantasy" product is not a
       thing, and naming the five here would describe a game nobody plays. The
       SPORT model above is the one with the wider competition scope. */
    /* "Projected points" is honest for the base scoring, which is exact. BONUS
       points are an estimate and the engine refuses to return an uncalibrated
       probability for them — so the blurb does not promise bonus, and Franco
       presents it as an estimate when asked. */
    blurb: "Captaincy, transfers and projected points for Fantasy Premier League.",
    build: "beta",
    /* ON SALE since 2026-08-29, matching RIPK a4c31f2. GST-inclusive price,
       verified live before it was pasted. */
    /* OFF SALE 2026-10-06 (Ned): Premium and Pro are the only subscriptions on
       sale. `beta` keeps it reachable to both tiers; existing per-model
       subscribers keep access through their SUB#MODEL row. The priceId stays so
       re-opening sales is this one flag, in both repos (RIPK api/catalogue.json). */
    purchasable: false,
    priceId: "price_1UBZR02Ni8JNdVHwACArqzfH",
    monthlyPriceAud: STANDARD_PER_MODEL_PRICE_AUD,
    /* Sportmonks, not API-Sports. Defensive Contribution needs clearances and
       ball recoveries; API-Sports carries neither, so without this provider
       there is no Franco. */
    dataProvider: "sportmonks",
  },
];

export const SPORTS: readonly Sport[] = ["afl", "nba", "nfl", "football"];
export const MODES: readonly Mode[] = ["sport", "fantasy"];

export function findModel(id: string): CatalogueSeed | undefined {
  return CATALOGUE.find((m) => m.id === id);
}

/**
 * A model is reachable only if it actually answers questions.
 * Takes the minimum it needs, so it works on a seed or a fetched entry alike.
 */
export function isReachable(m: Pick<ModelEntry, "build">): boolean {
  return m.build === "live" || m.build === "beta";
}

/** Drives "Included today: …" copy so the pricing page cannot overclaim. */
export function reachableModels(): readonly CatalogueSeed[] {
  return CATALOGUE.filter(isReachable);
}

/**
 * What a customer is charged today, and what it reverts to.
 *
 * Every surface that shows a per-model price goes through this. Three places
 * render one (the model card, the pricing grid, the locked-model upsell), and
 * before this existed they each read `weeklyPriceAud` directly — so introducing
 * a promotion made two of them advertise $19.99 while the third said $7.99.
 *
 * `note` is built here rather than at each call site so the three surfaces can't
 * word the same promise three ways. It states both halves Terms §6 requires: what
 * this subscriber keeps paying, and what someone joining later will pay.
 *
 * `billedMonthly` USED TO BE PART OF THIS SHAPE and was removed on 2026-08-31.
 * While prices were quoted weekly it carried the only figure that ever appeared
 * on a bank statement, so every surface had to render both. Now that the quoted
 * rate IS the charged rate, a second field holding the same number is not
 * belt-and-braces — it is two things to keep equal, which is the failure this
 * function was written to prevent.
 */
export function priceToday(
  m: Pick<ModelEntry, "monthlyPriceAud" | "promoPriceAud" | "promoLabel">,
): {
  /** Per MONTH — the headline figure, and exactly what is charged. */
  now: number;
  /** Per month, after the promotion ends. */
  revertsTo: number | null;
  note: string | null;
} | null {
  if (m.monthlyPriceAud === undefined) return null;
  if (m.promoPriceAud === undefined) {
    return { now: m.monthlyPriceAud, revertsTo: null, note: null };
  }
  const label = m.promoLabel ?? "Promotional rate";
  /* What this subscriber keeps paying, and what someone joining later pays —
     the two halves Terms §6 requires. */
  return {
    now: m.promoPriceAud,
    revertsTo: m.monthlyPriceAud,
    note: `${label}. $${m.monthlyPriceAud.toFixed(2)} a month after launch.`,
  };
}
